<script>
import AttachmentFilePicker from '@/components/ui/AttachmentFilePicker.vue'

export default {
  name: 'DocumentAttachmentUploadForm',
  components: { AttachmentFilePicker },
  props: {
    modelValue: {
      type: [Object, Array],
      default: null
    },
    fileName: {
      type: String,
      default: ''
    },
    valid: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    multiple: { type: Boolean, default: false },
    showFooter: { type: Boolean, default: true },
    uploadDisabled: { type: Boolean, default: false },
    showFileName: {
      type: Boolean,
      default: true
    },
    showMobileUpload: {
      type: Boolean,
      default: true
    },
    fileNameLabel: {
      type: String,
      default: ''
    },
    fileNamePlaceholder: {
      type: String,
      default: ''
    },
    fileLabel: {
      type: String,
      required: true
    },
    acceptedFormatsLabel: {
      type: String,
      required: true
    },
    mobileUploadLabel: {
      type: String,
      default: ''
    },
    fileNameRules: {
      type: Array,
      default: () => []
    },
    fileRules: {
      type: Array,
      default: () => []
    },
    maxFileNameLength: {
      type: Number,
      default: 255
    },
    accept: {
      type: String,
      default: '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg'
    }
  },
  emits: [
    'update:modelValue',
    'update:fileName',
    'update:valid',
    'submit',
    'cancel',
    'openMobileUpload'
  ],
  computed: {
    localFile: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    localFileName: {
      get() {
        return this.fileName
      },
      set(value) {
        this.$emit('update:fileName', value)
      }
    },
    localValid: {
      get() {
        return this.valid
      },
      set(value) {
        this.$emit('update:valid', value)
      }
    }
  }
}
</script>

<template>
  <v-form v-model="localValid" class="modal-card__form" @submit.prevent="$emit('submit')">
    <div class="modal-card__body px-4 px-sm-6 pb-4 pt-4">
      <slot name="before-fields" />

      <template v-if="showFileName">
        <p class="custom-label" required="true">{{ fileNameLabel }}</p>
        <v-text-field
          v-model.trim="localFileName"
          :maxlength="maxFileNameLength"
          :placeholder="fileNamePlaceholder"
          :rules="fileNameRules"
          :disabled="loading"
          required
        />
      </template>

      <p class="custom-label" required="true">{{ fileLabel }}</p>
      <div :class="{ 'document-attachment-actions': showMobileUpload }">
        <AttachmentFilePicker
          v-model="localFile"
          :label="showMobileUpload ? $t('ATTACHMENT_UPLOAD.FROM_COMPUTER') : acceptedFormatsLabel"
          :rules="fileRules"
          :accept="accept"
          :multiple="multiple"
          :disabled="loading || uploadDisabled"
          class="document-attachment-file-input"
        />

        <v-btn-secondary
          v-if="showMobileUpload"
          class="document-attachment-mobile-button"
          color="default"
          :disabled="loading"
          size="default"
          type="button"
          variant="tonal"
          @click="$emit('openMobileUpload')"
        >
          <v-icon class="mr-2" icon="mdi-qrcode-scan" size="18" />
          {{ mobileUploadLabel }}
        </v-btn-secondary>
      </div>

      <slot name="additional-fields" />
    </div>

    <v-divider v-if="showFooter" />
    <div v-if="showFooter" class="modal-card__footer">
      <v-btn type="button" variant="tonal" :disabled="loading" @click="$emit('cancel')">
        {{ $t('COMMON.BUTTON.CANCEL') }}
      </v-btn>
      <v-btn :disabled="!localValid || loading || uploadDisabled" :loading="loading" type="submit">
        {{ $t('COMMON.BUTTON.VALIDATE') }}
      </v-btn>
    </div>
  </v-form>
</template>

<style scoped>
.document-attachment-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 10px;
}
.document-attachment-mobile-button,
.document-attachment-file-input :deep(.v-field) {
  min-height: 56px;
}
</style>
